<template>
  <div class="expenses">
    <Actions :items="actionsHeader" :right="true" />
    <MobileTable
      v-model:selection="store.state.expense.selectedExpenses"
      :label="listLabel"
      :columns="columns"
      :data="tableData"
    />
    <DesktopTable
      v-model:selection="store.state.expense.selectedExpenses"
      :label="listLabel"
      :columns="columns"
      :data="tableData"
    />
    <p v-if="showEmptyState" class="empty-note">No hay egresos para mostrar.</p>
    <Actions :items="actionsFooter" />
  </div>
</template>

<script>
import { computed, reactive, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import store from "../../store";
import Actions from "../../components/shared/Actions.vue";
import MobileTable from "../../widgets/tables/MobileTable.vue";
import DesktopTable from "../../widgets/tables/DesktopTable.vue";
import { openMode, actions } from "../../constants";

export default {
  components: { MobileTable, DesktopTable, Actions },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });
    const closures = computed(() => store.state.commonExpense?.closures || []);

    const isPeriodClosed = (dateStr) => {
      if (!dateStr || !buildingId.value || !closures.value.length) return false;
      const parsed = new Date(dateStr);
      if (Number.isNaN(parsed.getTime())) return false;
      const month = parsed.getUTCMonth() + 1;
      const year = parsed.getUTCFullYear();
      return closures.value.some(
        (closure) =>
          Number(closure.buildingId) === Number(buildingId.value) &&
          Number(closure.month) === month &&
          Number(closure.year) === year &&
          closure.status === "closed"
      );
    };

    const loadClosures = (targetBuildingId = buildingId.value) => {
      if (!targetBuildingId) return Promise.resolve();
      return store.dispatch(actions.commonExpenseActions.findAllClosure, {
        buildingId: targetBuildingId,
      });
    };

    const listLabel = computed(() => route.name || "Egresos");

    const tableData = computed(() =>
      (store.state.expense.expenses || []).map((item) => ({
        id: item.id,
        number: item.number,
        date: item.date,
        supplier: item.supplier?.name || "",
        account: item.account?.name || "",
        paymentMethod: item.paymentMethod?.name || "",
        amount: item.amount,
        comment: item.comment || "",
      }))
    );

    const columns = reactive([
      { field: "number", header: "Numero", sort: true },
      { field: "date", header: "Fecha", sort: true, date: true },
      { field: "supplier", header: "Proveedor" },
      { field: "account", header: "Cuenta" },
      { field: "paymentMethod", header: "Forma de pago" },
      { field: "amount", header: "Monto", currency: true },
      { field: "comment", header: "Comentario" },
    ]);

    const showEmptyState = computed(
      () => !store.state.loading && !tableData.value.length && Boolean(buildingId.value)
    );

    const ensureBreadcrumb = () => {
      const exists = store.state.general.breadcrumbs.find((item) => item.to === route.path);
      if (!exists) {
        store.commit("addBreadcrumb", { label: route.name, to: route.path });
      }
    };

    const loadExpenses = async () => {
      const currentBuildingId = buildingId.value;
      if (!currentBuildingId) {
        return;
      }
      await Promise.all([
        loadClosures(currentBuildingId),
        store.dispatch(actions.expenseActions.findAll, {
          buildingId: currentBuildingId,
        }),
      ]);
    };

    const onCreate = () => {
      store.commit("setOpenMode", openMode.CREATE);
      router.push("/contabilidad/egreso/nuevo");
    };

    const onModify = () => {
      if (store.state.expense.selectedExpenses.length !== 1) {
        alert("Debe seleccionar un registro");
        return;
      }
      const selected = store.state.expense.selectedExpenses[0] || null;
      if (selected) {
        store.commit("setCurrentExpense", selected);
      }
      store.commit("setOpenMode", openMode.MODIFY);
      router.push("/contabilidad/egreso/modificar");
    };

    const onDelete = async () => {
      if (!store.state.expense.selectedExpenses.length) {
        alert("Debe seleccionar al menos un registro");
        return;
      }
      await loadClosures();
      const lockedExpenses = store.state.expense.selectedExpenses.filter((expense) =>
        isPeriodClosed(expense.date)
      );
      if (lockedExpenses.length) {
        const label = lockedExpenses
          .map((item) => item.number || item.id)
          .join(", ");
        alert(
          `No es posible eliminar egresos correspondientes a periodos cerrados (${label}).`
        );
        return;
      }
      if (!confirm("Eliminar los egresos seleccionados?")) return;
      store.commit("setLoading", true);
      try {
        const errors = [];
        for (const expense of store.state.expense.selectedExpenses) {
          const payload = {
            id: expense.id,
            buildingId: buildingId.value,
          };
          try {
            const response = await store.dispatch(actions.expenseActions.delete, payload);
            if (!response?.ok) {
              errors.push(response?.message || `Egreso ${expense.number || expense.id} no pudo eliminarse.`);
            }
          } catch (error) {
            console.error(error);
            errors.push(`Egreso ${expense.number || expense.id} no pudo eliminarse.`);
          }
        }
        if (errors.length) {
          store.commit("setToast", {
            type: "warn",
            message: errors[0],
          });
        } else {
          store.commit("setToast", {
            type: "success",
            message: "Egresos eliminados correctamente.",
          });
        }
        store.commit("setSelectedExpenses", []);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const onDownloadExcel = async () => {
      if (!buildingId.value) {
        alert("Debe seleccionar un edificio");
        return;
      }
      store.commit("setLoading", true);
      try {
        await store.dispatch(actions.expenseActions.downloadExcel, {
          buildingId: buildingId.value,
        });
      } finally {
        store.commit("setLoading", false);
      }
    };

    const goSuppliers = () => {
      router.push("/contabilidad/proveedor/listado");
    };

    const actionsHeader = reactive([
      {
        name: "Descargar Excel",
        action: onDownloadExcel,
        hidden: false,
      },
    ]);

    const actionsFooter = reactive([
      {
        name: "Nuevo",
        action: onCreate,
        hidden: router.getRoutes().find((r) => r.path === "/contabilidad/egreso/nuevo"),
      },
      {
        name: "Modificar",
        action: onModify,
        hidden: router.getRoutes().find((r) => r.path === "/contabilidad/egreso/modificar"),
      },
      {
        name: "Eliminar",
        action: onDelete,
        hidden: true,
      },
      {
        name: "Proveedores",
        action: goSuppliers,
        hidden: router.getRoutes().find((r) => r.path === "/contabilidad/proveedor/listado"),
      },
    ]);

    onMounted(async () => {
      store.commit("setLoading", true);
      store.commit("setSelectedExpenses", []);
      ensureBreadcrumb();
      try {
        await loadExpenses();
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        store.commit("setLoading", true);
        store.commit("setSelectedExpenses", []);
        try {
          await loadExpenses();
        } finally {
          store.commit("setLoading", false);
        }
      }
    });

    return {
      store,
      columns,
      tableData,
      actionsHeader,
      actionsFooter,
      listLabel,
      showEmptyState,
    };
  },
};
</script>

<style scoped>
.empty-note {
  margin: 1rem 0;
  text-align: center;
  color: #64748b;
}
</style>



